import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import CommonSelect from "../../components/common/CommonSelect";

const buildFormPayload = (templateData, flatFormState) => {
  const payload = {};
  if (templateData?.sections) {
    Object.keys(templateData.sections).forEach((sectionKey) => {
      payload[sectionKey] = {};
      const fields = templateData.sections[sectionKey].fields;
      if (fields) {
        Object.keys(fields).forEach((fieldKey) => {
          const field = fields[fieldKey];
          if (field.type === "group") {
            payload[sectionKey][fieldKey] = {};
            if (field.fields) {
              Object.keys(field.fields).forEach((groupFieldKey) => {
                payload[sectionKey][fieldKey][groupFieldKey] =
                  flatFormState[`${fieldKey}.${groupFieldKey}`] ?? "";
              });
            }
          } else if (field.branches) {
            const branchValue = flatFormState[fieldKey];
            const updatedBranches = {};
            Object.keys(field.branches).forEach((branchKey) => {
              const subQ = field.branches[branchKey].subQuestion;
              if (subQ) {
                const answerKey = `${fieldKey}_branch_${branchKey}`;
                updatedBranches[branchKey] = {
                  subQuestion: {
                    ...subQ,
                    value: flatFormState[answerKey] ?? null,
                  },
                };
              }
            });
            payload[sectionKey][fieldKey] = {
              value: branchValue !== undefined ? branchValue : null,
              branches: updatedBranches,
            };
          } else {
            payload[sectionKey][fieldKey] = flatFormState[fieldKey] ?? "";
          }
        });
      }
    });
  }
  return payload;
};

const isWrappedValue = (val) =>
  typeof val === "object" && val !== null && "value" in val;

const isRequiredAnswerMissing = (required, value) =>
  required &&
  (value === undefined ||
    value === null ||
    value === "" ||
    value === false ||
    (Array.isArray(value) && value.length === 0));

const getBranchWidth = (fieldType, columnsCount) => {
  if (fieldType === "textarea" || fieldType === "group") return "100%";
  if (columnsCount === 2) return "calc(50% - 12px)";
  if (columnsCount === 3) return "calc(33.333% - 16px)";
  if (columnsCount === 4) return "calc(25% - 18px)";
  return "100%";
};

const getInitialSubFieldValue = (subField, savedSubVal) => {
  if (savedSubVal !== undefined && savedSubVal !== null) {
    return isWrappedValue(savedSubVal) ? savedSubVal.value : savedSubVal;
  }
  if (subField.type === "boolean" || subField.type === "checkbox") {
    return subField.is_checked !== undefined
      ? subField.is_checked
      : (subField?.value ?? "");
  }
  if (subField.type === "multi_select") {
    return subField.options
      ? subField.options
          .filter((opt) => opt.is_selected)
          .map((opt) => opt.label)
      : [];
  }
  if (subField.type === "single_select") {
    return subField.options
      ? subField.options.find((opt) => opt.is_selected)?.label || ""
      : "";
  }
  return subField.value ?? "";
};

const getInitialMultiSelectValue = (field, savedVal) => {
  if (savedVal !== undefined && savedVal !== null) {
    if (isWrappedValue(savedVal)) {
      return savedVal.value || [];
    }
    return Array.isArray(savedVal) ? savedVal : [savedVal];
  }
  return field.options.filter((opt) => opt.is_selected).map((opt) => opt.label);
};

const getInitialBooleanValue = (field, savedVal) => {
  if (savedVal !== undefined && savedVal !== null) {
    return isWrappedValue(savedVal) ? savedVal.value : savedVal;
  }
  return field.is_checked || false;
};

const getInitialSingleSelectValue = (field, savedVal) => {
  if (savedVal !== undefined && savedVal !== null) {
    return isWrappedValue(savedVal) ? savedVal.value : savedVal;
  }
  return field.options.find((opt) => opt.is_selected)?.label || "";
};

const initBranchState = (initialState, fieldKey, field, savedVal) => {
  if (!field.branches) return;
  const savedBranches = savedVal?.branches || field.branches;
  Object.keys(field.branches).forEach((branchKey) => {
    const savedSubQ = savedBranches?.[branchKey]?.subQuestion;
    initialState[`${fieldKey}_branch_${branchKey}`] = savedSubQ?.value || "";
  });
};

const initGroupFieldState = (
  initialState,
  sectionKey,
  fieldKey,
  field,
  programFormPayload,
) => {
  if (!field.fields) return;
  Object.keys(field.fields).forEach((groupFieldKey) => {
    const subField = field.fields[groupFieldKey];
    const savedSubVal =
      programFormPayload?.[sectionKey]?.[fieldKey]?.[groupFieldKey];
    initialState[`${fieldKey}.${groupFieldKey}`] = getInitialSubFieldValue(
      subField,
      savedSubVal,
    );
  });
};

const initFieldState = (
  initialState,
  sectionKey,
  fieldKey,
  field,
  programFormPayload,
) => {
  const savedVal = programFormPayload?.[sectionKey]?.[fieldKey];
  if (field.type === "group") {
    initGroupFieldState(
      initialState,
      sectionKey,
      fieldKey,
      field,
      programFormPayload,
    );
  } else if (field.type === "multi_select") {
    initialState[fieldKey] = getInitialMultiSelectValue(field, savedVal);
    initBranchState(initialState, fieldKey, field, savedVal);
  } else if (field.type === "boolean" || field.type === "checkbox") {
    initialState[fieldKey] = getInitialBooleanValue(field, savedVal);
    initBranchState(initialState, fieldKey, field, savedVal);
  } else if (field.type === "single_select") {
    initialState[fieldKey] = getInitialSingleSelectValue(field, savedVal);
    initBranchState(initialState, fieldKey, field, savedVal);
  } else {
    initialState[fieldKey] = (savedVal ?? field.value) || "";
  }
};

const buildInitialFormState = (data, programFormPayload) => {
  const initialState = {};
  if (!data?.sections) return initialState;
  Object.keys(data.sections).forEach((sectionKey) => {
    const fields = data.sections[sectionKey].fields;
    Object.keys(fields).forEach((fieldKey) => {
      initFieldState(
        initialState,
        sectionKey,
        fieldKey,
        fields[fieldKey],
        programFormPayload,
      );
    });
  });
  return initialState;
};

const WomensHealth = ({
  memberData,
  onFormStateChange,
  primaryColor,
  secondaryColor,
  borderColor,
  programAssessmentSchema,
  programFormPayload,
  columnsCount = 2,
  readOnly = false,
  showValidationErrors = false,
}) => {
  console.log(
    "WomensHealth component function invoked, programAssessmentSchema prop:",
    programAssessmentSchema,
  );
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState({});
  const [anchorEls, setAnchorEls] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const selectRefs = React.useRef({});

  const handleOpenSelect = (fieldKey, event) => {
    if (readOnly) return;
    setAnchorEls((prev) => ({ ...prev, [fieldKey]: event.currentTarget }));
  };

  const handleCloseSelect = (fieldKey) => {
    setAnchorEls((prev) => ({ ...prev, [fieldKey]: null }));
  };

  useEffect(() => {
    if (!loading && onFormStateChange && assessmentData) {
      const nestedPayload = buildFormPayload(assessmentData, formState);
      onFormStateChange(nestedPayload);
    }
  }, [formState, assessmentData, onFormStateChange, loading]);

  useEffect(() => {
    const loadAssessment = () => {
      console.log(
        "WomensHealth loadAssessment called, programAssessmentSchema:",
        programAssessmentSchema,
      );
      setLoading(true);
      const data = programAssessmentSchema;
      console.log("WomensHealth final assessment data set to:", data);
      setAssessmentData(data);

      // Initialize form state
      const initialState = buildInitialFormState(data, programFormPayload);
      console.log("WomensHealth initialized initialState:", initialState);
      setFormState(initialState);
      setLoading(false);
    };

    loadAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberData.memberId, programAssessmentSchema]);

  const handleFieldChange = (fieldKey, value) => {
    setFormState((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const isVisible = (fieldKey, field) => {
    if (!field.dependency) return true;

    const { key, value } = field.dependency;
    const actualValue = formState[key];

    if (Array.isArray(value)) {
      return value.includes(actualValue);
    }
    return actualValue === value;
  };

  const toRadioValue = (val) => {
    if (val === true) return "yes";
    if (val === false) return "no";
    return "";
  };

  const toSelectedValuesArray = (isMultiple, fieldValue) => {
    if (isMultiple) return fieldValue || [];
    if (fieldValue) return [fieldValue];
    return [];
  };

  const resolveSubFieldType = (subQType, isFocusedOrHasValue) => {
    if (subQType === "time") {
      return isFocusedOrHasValue ? "time" : "text";
    }
    if (subQType === "number") {
      return "number";
    }
    return "text";
  };

  const resolveFieldInputType = (fieldType, isFocusedOrHasValue) => {
    if (fieldType === "time") {
      return isFocusedOrHasValue ? "time" : "text";
    }
    if (fieldType === "textarea") {
      return "text";
    }
    return fieldType;
  };

  const getFieldPlaceholderText = (field) => {
    if (field.type === "textarea") {
      return "Enter notes...";
    }
    if ((field.label || "").length > 14) {
      return "Enter...";
    }
    return `Enter ${field.label || "value"}`;
  };

  const resolveHoverBorderColor = (
    readOnly,
    isMissingRequired,
    effectiveBorderColor,
    primaryColor,
  ) => {
    if (readOnly) return effectiveBorderColor;
    if (isMissingRequired) return "#E05353";
    return primaryColor;
  };

  const getSelectDisplayText = (selectedValues, fieldLabel) => {
    if (selectedValues.length > 0) {
      return selectedValues.join(", ");
    }
    if (fieldLabel.length > 14) {
      return "Select...";
    }
    return `Select ${fieldLabel}...`;
  };

  const isValueEmpty = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  const getIsGroupMissing = (
    hasSubFields,
    fieldRequired,
    hasMissingRequiredSubField,
    hasAnyEmptySubField,
    value,
  ) => {
    if (hasSubFields) {
      if (fieldRequired) {
        return hasMissingRequiredSubField || hasAnyEmptySubField;
      }
      return hasMissingRequiredSubField;
    }
    return fieldRequired && isValueEmpty(value);
  };

  const renderFieldLabel = (field) => (
    <Typography
      sx={{
        fontSize: "13px",
        color: "#403C6F",
        fontWeight: 500,
        mb: 1,
        display: "block",
        letterSpacing: "0.5px",
      }}
    >
      {field.label}{" "}
      {field.required && <span style={{ color: "#E05353" }}>*</span>}
    </Typography>
  );

  const handleDateChange = (fieldKey, newValue) => {
    if (newValue === null) {
      handleFieldChange(fieldKey, null);
    } else if (newValue.isValid()) {
      handleFieldChange(fieldKey, newValue.format("YYYY-MM-DD"));
    }
  };

  const handleSelectAllChange = (fieldKey, selectedValues, options) => {
    const isAllSelected = selectedValues.length === options.length;
    handleFieldChange(fieldKey, isAllSelected ? [] : options);
  };

  const handleSelectTypeToggle = (
    fieldKey,
    isMultiple,
    selectedValues,
    type,
  ) => {
    if (isMultiple) {
      const newValue = selectedValues.includes(type)
        ? selectedValues.filter((v) => v !== type)
        : [...selectedValues, type];
      handleFieldChange(fieldKey, newValue);
    } else {
      handleFieldChange(fieldKey, type);
      handleCloseSelect(fieldKey);
    }
  };

  const getSelectWidth = (fieldKey) =>
    selectRefs.current[fieldKey]?.offsetWidth
      ? `${selectRefs.current[fieldKey].offsetWidth - 50}px`
      : 200;

  const renderSubCheckbox = (
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
  ) => {
    return (
      <FormControlLabel
        sx={{ ml: -0.5 }}
        control={
          <Checkbox
            size="small"
            checked={!!subFieldValue}
            onChange={(e) => handleFieldChange(subFieldKey, e.target.checked)}
            disabled={readOnly}
            sx={{
              p: 0.5,
              color:
                isSubMissing && !subFieldValue
                  ? "#E05353 !important"
                  : borderColor,
              "&.Mui-checked": { color: `${primaryColor} !important` },
              "& .MuiSvgIcon-root": {
                color:
                  isSubMissing && !subFieldValue
                    ? "#E05353 !important"
                    : undefined,
              },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              color: isSubMissing && !subFieldValue ? "#E05353" : "#10100F",
            }}
          >
            {subQ.label || "Yes"}
          </Typography>
        }
      />
    );
  };

  const renderSubSelect = (subFieldKey, subQ, subFieldValue) => {
    const isMultiple = subQ.type === "multi_select";
    const selectedValues = toSelectedValuesArray(isMultiple, subFieldValue);
    const options = (subQ.options || []).map((opt) =>
      typeof opt === "string" ? opt : opt.label,
    );

    return (
      <Box>
        <Box
          ref={(el) => (selectRefs.current[subFieldKey] = el)}
          onClick={(e) => handleOpenSelect(subFieldKey, e)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "40px",
            px: 1.5,
            borderRadius: "8px",
            border: `1px solid ${borderColor}`,
            backgroundColor: readOnly ? "#F3F4F6" : "#fff",
            cursor: readOnly ? "not-allowed" : "pointer",
            "&:hover": {
              borderColor: readOnly ? borderColor : primaryColor,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: selectedValues.length > 0 ? "#10100F" : "#9F9DB6",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedValues.length > 0
              ? selectedValues.join(", ")
              : `Select ${subQ.label}...`}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: "#6B7280", fontSize: "20px" }} />
        </Box>
        <CommonSelect
          id={`${subFieldKey}-select`}
          open={Boolean(anchorEls[subFieldKey])}
          anchorEl={anchorEls[subFieldKey]}
          onClose={() => handleCloseSelect(subFieldKey)}
          noteTypes={options}
          selectedTypes={selectedValues}
          multiple={isMultiple}
          handleSelectAll={() =>
            handleSelectAllChange(subFieldKey, selectedValues, options)
          }
          handleTypeToggle={(type) =>
            handleSelectTypeToggle(
              subFieldKey,
              isMultiple,
              selectedValues,
              type,
            )
          }
          width={getSelectWidth(subFieldKey)}
        />
      </Box>
    );
  };

  const renderSubFieldInput = (subFieldKey, subQ, subFieldValue) => {
    const isSubMissing = isRequiredAnswerMissing(subQ.required, subFieldValue);
    const subEffectiveBorderColor = isSubMissing ? "#E05353" : borderColor;

    switch (subQ.type) {
      case "textarea":
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={4}
            value={subFieldValue || ""}
            onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
            placeholder={subQ.placeholder || "Enter notes..."}
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": { borderColor: subEffectiveBorderColor },
                "&:hover fieldset": {
                  borderColor: isSubMissing ? "#E05353" : primaryColor,
                },
              },
            }}
          />
        );
      case "boolean":
        return (
          <RadioGroup
            row
            value={toRadioValue(subFieldValue)}
            onChange={(e) =>
              handleFieldChange(subFieldKey, e.target.value === "yes")
            }
          >
            <FormControlLabel
              value="yes"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: `${primaryColor} !important` },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  Yes
                </Typography>
              }
            />
            <FormControlLabel
              value="no"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: `${primaryColor} !important` },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  No
                </Typography>
              }
            />
          </RadioGroup>
        );
      case "checkbox":
        return renderSubCheckbox(
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
        );
      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={subFieldValue ? dayjs(subFieldValue) : null}
              onChange={(newValue) => handleDateChange(subFieldKey, newValue)}
              disabled={readOnly}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor },
                      "&:hover fieldset": { borderColor: primaryColor },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      case "single_select":
      case "multi_select":
        return renderSubSelect(subFieldKey, subQ, subFieldValue);
      case "text":
        return (
          <TextField
            fullWidth
            size="small"
            inputProps={{ maxLength: 255 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#9F9DB6",
                      userSelect: "none",
                    }}
                  >
                    {subFieldValue ? `${subFieldValue.length}/255` : "0/255"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            value={subFieldValue || ""}
            onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
            placeholder={subQ.placeholder || `Enter ${subQ.label || "value"}`}
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                pr: 1,
                "& fieldset": { borderColor },
                "&:hover fieldset": { borderColor: primaryColor },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        );
      case "time":
      case "number":
      default:
        return (
          <TextField
            fullWidth
            size="small"
            type={resolveSubFieldType(
              subQ.type,
              focusedField === subFieldKey || subFieldValue,
            )}
            value={subFieldValue || ""}
            onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
            placeholder={
              subQ.placeholder ||
              (subQ.type === "time"
                ? "HH:MM AM/PM"
                : `Enter ${subQ.label || "value"}`)
            }
            onFocus={() => setFocusedField(subFieldKey)}
            onBlur={() => setFocusedField(null)}
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": { borderColor },
                "&:hover fieldset": { borderColor: primaryColor },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        );
    }
  };

  const renderBranchingQuestions = (fieldKey, field) => {
    if (!field.branches) return null;

    const value = formState[fieldKey];
    const activeBranches = [];

    if (field.type === "boolean") {
      if (value === true && field.branches["true"]) {
        activeBranches.push({ key: "true", config: field.branches["true"] });
      } else if (value === false && field.branches["false"]) {
        activeBranches.push({ key: "false", config: field.branches["false"] });
      }
    } else if (field.type === "single_select") {
      if (value && field.branches[value]) {
        activeBranches.push({ key: value, config: field.branches[value] });
      }
    } else if (field.type === "multi_select") {
      const selected = Array.isArray(value) ? value : [];
      selected.forEach((opt) => {
        if (field.branches[opt]) {
          activeBranches.push({ key: opt, config: field.branches[opt] });
        }
      });
    }

    if (activeBranches.length === 0) return null;

    return (
      <>
        {activeBranches.map(({ key: branchKey, config }) => {
          const subQ = config.subQuestion;
          if (!subQ) return null;
          const subFieldKey = `${fieldKey}_branch_${branchKey}`;
          const subFieldValue = formState[subFieldKey];

          const subWidth = getBranchWidth(subQ.type, columnsCount);

          return (
            <Box
              key={subFieldKey}
              sx={{
                width: subWidth,
                display: "flex",
                flexDirection: "column",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#403C6F",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                  letterSpacing: "0.5px",
                }}
              >
                {subQ.label}{" "}
                {subQ.required && <span style={{ color: "#E05353" }}>*</span>}
              </Typography>
              <Box sx={{ mt: "auto" }}>
                {renderSubFieldInput(subFieldKey, subQ, subFieldValue)}
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  const getFieldState = (fieldKey, field) => {
    const value = formState[fieldKey];
    const isMissingRequired = isRequiredAnswerMissing(field.required, value);
    return {
      value,
      isMissingRequired,
      effectiveBorderColor: isMissingRequired ? "#E05353" : borderColor,
    };
  };

  const renderTextFieldInput = (fieldKey, field) => {
    const { value, isMissingRequired, effectiveBorderColor } = getFieldState(
      fieldKey,
      field,
    );

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <TextField
            fullWidth
            size="small"
            inputProps={{ maxLength: 255 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#9F9DB6",
                      userSelect: "none",
                    }}
                  >
                    {value ? `${value.length}/255` : "0/255"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            value={value || ""}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            placeholder={field.placeholder || getFieldPlaceholderText(field)}
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                pr: 1,
                "& fieldset": {
                  borderColor: `${effectiveBorderColor} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderTimeNumberTextareaField = (fieldKey, field) => {
    const { value, isMissingRequired, effectiveBorderColor } = getFieldState(
      fieldKey,
      field,
    );

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <TextField
            fullWidth
            size="small"
            type={resolveFieldInputType(
              field.type,
              focusedField === fieldKey || value,
            )}
            multiline={field.type === "textarea"}
            rows={field.type === "textarea" ? 4 : 1}
            value={value || ""}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            placeholder={
              field.placeholder ||
              (field.type === "time"
                ? "HH:MM AM/PM"
                : getFieldPlaceholderText(field))
            }
            onFocus={() => setFocusedField(fieldKey)}
            onBlur={() => setFocusedField(null)}
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": {
                  borderColor: `${effectiveBorderColor} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderDateField = (fieldKey, field) => {
    const { value, isMissingRequired, effectiveBorderColor } = getFieldState(
      fieldKey,
      field,
    );

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={value ? dayjs(value) : null}
              onChange={(newValue) => handleDateChange(fieldKey, newValue)}
              disabled={readOnly}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: isMissingRequired,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: `${effectiveBorderColor} !important`,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${effectiveBorderColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>
    );
  };

  const renderSelectField = (fieldKey, field) => {
    const { value, isMissingRequired, effectiveBorderColor } = getFieldState(
      fieldKey,
      field,
    );

    const isMultiple = field.type === "multi_select";
    const selectedValues = toSelectedValuesArray(isMultiple, value);
    const options = (field.options || []).map((opt) => opt.label);

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <Box
            ref={(el) => (selectRefs.current[fieldKey] = el)}
            onClick={(e) => handleOpenSelect(fieldKey, e)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
              px: 1.5,
              borderRadius: "8px",
              border: `1px solid ${effectiveBorderColor}`,
              backgroundColor: readOnly ? "#F3F4F6" : "#fff",
              cursor: readOnly ? "not-allowed" : "pointer",
              "&:hover": {
                borderColor: resolveHoverBorderColor(
                  readOnly,
                  isMissingRequired,
                  effectiveBorderColor,
                  primaryColor,
                ),
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: selectedValues.length > 0 ? "#10100F" : "#9F9DB6",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getSelectDisplayText(selectedValues, field.label)}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ color: "#6B7280", fontSize: "20px" }}
            />
          </Box>
          <CommonSelect
            id={`${fieldKey}-select`}
            open={Boolean(anchorEls[fieldKey])}
            anchorEl={anchorEls[fieldKey]}
            onClose={() => handleCloseSelect(fieldKey)}
            noteTypes={options}
            selectedTypes={selectedValues}
            multiple={isMultiple}
            handleSelectAll={() =>
              handleSelectAllChange(fieldKey, selectedValues, options)
            }
            handleTypeToggle={(type) =>
              handleSelectTypeToggle(fieldKey, isMultiple, selectedValues, type)
            }
            width={getSelectWidth(fieldKey)}
          />
        </Box>
      </Box>
    );
  };

  const renderBooleanField = (fieldKey, field) => {
    const { value } = getFieldState(fieldKey, field);

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <RadioGroup
            row
            value={toRadioValue(value)}
            onChange={(e) =>
              handleFieldChange(fieldKey, e.target.value === "yes")
            }
          >
            <FormControlLabel
              value="yes"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: "#403C6F !important" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  Yes
                </Typography>
              }
            />
            <FormControlLabel
              value="no"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: "#403C6F !important" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  No
                </Typography>
              }
            />
          </RadioGroup>
        </Box>
      </Box>
    );
  };

  const renderCheckboxField = (fieldKey, field) => {
    const { value, isMissingRequired } = getFieldState(fieldKey, field);

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {renderFieldLabel(field)}
        <Box sx={{ mt: "auto" }}>
          <FormControlLabel
            sx={{ ml: -0.5 }}
            control={
              <Checkbox
                size="small"
                checked={!!value}
                onChange={(e) => handleFieldChange(fieldKey, e.target.checked)}
                disabled={readOnly}
                sx={{
                  p: 0.5,
                  color:
                    isMissingRequired && !value
                      ? "#E05353 !important"
                      : borderColor,
                  "&.Mui-checked": { color: "#403C6F !important" },
                  "& .MuiSvgIcon-root": {
                    color:
                      isMissingRequired && !value
                        ? "#E05353 !important"
                        : undefined,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: isMissingRequired && !value ? "#E05353" : "#10100F",
                }}
              >
                {field.checkboxLabel || "Yes / Completed"}
              </Typography>
            }
          />
        </Box>
      </Box>
    );
  };

  const renderGroupField = (fieldKey, field) => {
    const { value } = getFieldState(fieldKey, field);

    const hasSubFields =
      field.fields &&
      typeof field.fields === "object" &&
      Object.keys(field.fields).length > 0;
    const subKeys = hasSubFields ? Object.keys(field.fields) : [];

    const hasMissingRequiredSubField = subKeys.some(
      (subKey) =>
        field.fields[subKey]?.required &&
        isValueEmpty(formState[`${fieldKey}.${subKey}`]),
    );

    const hasAnyEmptySubField = subKeys.some((subKey) =>
      isValueEmpty(formState[`${fieldKey}.${subKey}`]),
    );

    const isGroupMissing = getIsGroupMissing(
      hasSubFields,
      field.required,
      hasMissingRequiredSubField,
      hasAnyEmptySubField,
      value,
    );
    const groupBorderColor =
      (showValidationErrors || isGroupMissing) && isGroupMissing
        ? "#E05353"
        : borderColor;

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          border: `1px solid ${groupBorderColor} !important`,
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 2,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {field.fields &&
            Object.keys(field.fields).map((groupFieldKey) => (
              <Box
                key={groupFieldKey}
                sx={{
                  width: {
                    xs: "100%",
                    sm: "calc(50% - 8px)",
                    md: "calc(25% - 12px)",
                  },
                }}
              >
                {renderField(
                  `${fieldKey}.${groupFieldKey}`,
                  field.fields[groupFieldKey],
                )}
              </Box>
            ))}
        </Box>
      </Box>
    );
  };

  const renderField = (fieldKey, field) => {
    switch (field.type) {
      case "text":
        return renderTextFieldInput(fieldKey, field);
      case "time":
      case "number":
      case "textarea":
        return renderTimeNumberTextareaField(fieldKey, field);
      case "date":
        return renderDateField(fieldKey, field);
      case "single_select":
      case "multi_select":
        return renderSelectField(fieldKey, field);
      case "boolean":
        return renderBooleanField(fieldKey, field);
      case "checkbox":
        return renderCheckboxField(fieldKey, field);
      case "group":
        return renderGroupField(fieldKey, field);
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress sx={{ color: primaryColor }} />
      </Box>
    );
  }

  if (
    !assessmentData?.sections ||
    Object.keys(assessmentData.sections).length === 0
  ) {
    return (
      <Box sx={{ mt: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: "12px",
            border: `1px solid ${borderColor}`,
            textAlign: "center",
            backgroundColor: "#F7F8FB",
          }}
        >
          <Typography variant="body1" sx={{ color: secondaryColor }}>
            No assessment template or questions have been configured for the
            selected program and assessment type. Please contact your
            administrator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      component="fieldset"
      disabled={readOnly}
      sx={{
        border: "none",
        padding: 0,
        margin: 0,
        minWidth: 0,
        mt: 3,
      }}
    >
      {Object.keys(assessmentData.sections).map((sectionKey) => {
        const section = assessmentData.sections[sectionKey];
        return (
          <Box key={sectionKey} sx={{ mb: 4 }}>
            <Box
              sx={{
                backgroundColor: "#ECECF1",
                p: 1.5,
                mb: 3,
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: primaryColor, fontSize: "15px" }}
              >
                {section.label}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "stretch",
              }}
            >
              {Object.keys(section.fields).map((fieldKey) => {
                const field = section.fields[fieldKey];
                if (!field?.type || !isVisible(fieldKey, field)) return null;

                const supportedTypes = [
                  "text",
                  "date",
                  "time",
                  "number",
                  "textarea",
                  "single_select",
                  "multi_select",
                  "boolean",
                  "checkbox",
                  "group",
                ];
                if (!supportedTypes.includes(field.type)) return null;

                const width = getBranchWidth(field.type, columnsCount);

                return (
                  <React.Fragment key={fieldKey}>
                    <Box
                      sx={{ width, display: "flex", flexDirection: "column" }}
                    >
                      {renderField(fieldKey, field)}
                    </Box>
                    {renderBranchingQuestions(fieldKey, field)}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

WomensHealth.propTypes = {
  memberData: PropTypes.object.isRequired,
  onFormStateChange: PropTypes.func,
  primaryColor: PropTypes.string.isRequired,
  secondaryColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  programAssessmentSchema: PropTypes.object,
  programFormPayload: PropTypes.object,
  columnsCount: PropTypes.number,
  readOnly: PropTypes.bool,
};

export default WomensHealth;
